import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle2, Loader2, SendHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchBookingActionContext,
  fetchCohostCancelContext,
  submitCohostCancel,
  submitBookingActionToken,
} from '../api/bookingActionApi';
import BookingActionShell from '../components/BookingActionShell';
import BookingConfirmOverlay from '../components/BookingConfirmOverlay';
import BookingMultiSlotPicker from '../components/BookingMultiSlotPicker';
import BookingSlotOptionList from '../components/BookingSlotOptionList';
import BookingSummaryCard from '../components/BookingSummaryCard';
import {
  BOOKING_ACTION_LABELS,
  buildSuggestedSlotPayloadsFromSlots,
  resolveAlreadyUsedActionMessage,
  resolveBookingActionMessage,
  resolveCompletedBookingAction,
} from '../utils/bookingActionUtils';
import {
  resolveBookingActionToken,
  resolveBookingActionValue,
} from '../utils/bookingActionLinkUtils';
import { resolveHostSlotDurationMinutes } from '../../HireWithUs/Pages/Applications/meetingScheduleUtils';
import '../LiveInterview.css';

function showAlreadyUsedCompletedState({
  context,
  requestedAction,
  setShowRescheduleForm,
  setResult,
  setCompletedAction,
  setCompleted,
  setError,
}) {
  const actor = context?.actor || 'candidate';
  const completedAction = resolveCompletedBookingAction(context) || requestedAction;
  const message = resolveAlreadyUsedActionMessage(context, requestedAction, actor);

  setShowRescheduleForm(false);
  setResult({
    action: completedAction,
    message,
    alreadyUsed: true,
  });
  setCompletedAction(completedAction);
  setCompleted(true);
  setError('');
}

export default function InterviewBookingManagePage() {
  const [searchParams] = useSearchParams();
  const cohostCancelToken = String(searchParams.get('cohost_cancel_token') || '').trim();

  if (cohostCancelToken) {
    return <CohostCancelInterviewPage token={cohostCancelToken} />;
  }
  return <InterviewBookingActionPage />;
}

function CohostCancelInterviewPage({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [finishedMessage, setFinishedMessage] = useState('');

  useEffect(() => {
    let active = true;
    const loadContext = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchCohostCancelContext(token);
        if (!active) return;
        setContext(payload);
        if (!payload?.can_cancel) {
          setFinishedMessage(
            payload?.message || 'This interview is no longer available for cancellation.'
          );
        }
      } catch (err) {
        if (!active) return;
        setError(err?.message || 'Unable to open this cancellation link.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadContext();
    return () => {
      active = false;
    };
  }, [token]);

  const handleBack = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (typeof window !== 'undefined') window.close();
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    const cleanReason = String(reason || '').trim();
    if (!cleanReason) {
      setReasonError('Please enter a cancellation reason.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = await submitCohostCancel({ token, reason: cleanReason });
      setFinishedMessage(
        payload?.message || 'Interview cancelled successfully.'
      );
      setContext(payload);
    } catch (err) {
      if (err?.payload?.already_finished) {
        setFinishedMessage(
          err?.payload?.message || 'This interview has already been cancelled.'
        );
        return;
      }
      setError(err?.message || 'Unable to cancel this interview.');
    } finally {
      setSubmitting(false);
    }
  }, [reason, token]);

  if (loading) {
    return (
      <BookingActionShell title="Cancel interview" subtitle="Loading interview details...">
        <div className="li-booking-loading">
          <Loader2 className="li-booking-btn-spinner" size={28} />
        </div>
      </BookingActionShell>
    );
  }

  if (finishedMessage) {
    return (
      <BookingActionShell title="Cancel interview">
        <p className="li-booking-inline-notice" role="status">
          <strong>{finishedMessage}</strong>
        </p>
      </BookingActionShell>
    );
  }

  if (error || !context?.can_cancel) {
    return (
      <BookingActionShell title="Link unavailable">
        <div className="li-booking-inline-error" role="alert">
          {error || 'This cancellation link is no longer available.'}
        </div>
      </BookingActionShell>
    );
  }

  return (
    <BookingActionShell
      title="Cancel interview"
      subtitle="Provide a reason before cancelling this confirmed interview."
    >
      <div className="li-booking-availability">
        <p className="li-booking-section-copy">
          {context?.slot_display || 'Confirmed interview'}
        </p>
        <label htmlFor="cohost-cancel-reason" className="block text-sm font-medium text-slate-700">
          Reason <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="cohost-cancel-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (reasonError) setReasonError('');
          }}
          placeholder="Why are you cancelling this interview?"
          rows={4}
          maxLength={500}
          disabled={submitting}
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
        />
        {reasonError ? <p className="mt-2 text-xs text-rose-600">{reasonError}</p> : null}
        {error ? <p className="mt-2 text-xs text-rose-600">{error}</p> : null}
        <div className="li-booking-submit-bar">
          <button
            type="button"
            className="li-booking-back-link"
            disabled={submitting}
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <button
            type="button"
            className="li-booking-btn li-booking-btn-danger"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? <Loader2 className="li-booking-btn-spinner" size={16} /> : <X size={16} />}
            Cancel Interview
          </button>
        </div>
      </div>
    </BookingActionShell>
  );
}

function InterviewBookingActionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => resolveBookingActionToken({ searchParams, location, params }),
    [location, params, searchParams]
  );
  const initialAction = useMemo(
    () => resolveBookingActionValue({ searchParams, location, params }),
    [location, params, searchParams]
  );
  const directActionMode = ['accept', 'decline', 'reschedule', 'next_slot'].includes(initialAction)
    ? initialAction
    : '';
  const isDirectAcceptFlow = directActionMode === 'accept';
  const isDirectRescheduleFlow = directActionMode === 'reschedule';

  const [contextLoading, setContextLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [completedAction, setCompletedAction] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineReasonError, setDeclineReasonError] = useState('');
  const [showRescheduleForm, setShowRescheduleForm] = useState(isDirectRescheduleFlow);
  const [selectedSlotValue, setSelectedSlotValue] = useState('');
  const [availabilityNotes, setAvailabilityNotes] = useState('');
  const [customSuggestedSlots, setCustomSuggestedSlots] = useState([]);
  const [customSlotsResetKey, setCustomSlotsResetKey] = useState(0);
  const autoAcceptStartedRef = useRef(false);

  const loadContext = useCallback(async () => {
    if (!token) {
      setError('This interview link is missing or invalid. Open the link from your email again.');
      setContextLoading(false);
      return;
    }
    setContextLoading(true);
    if (!isDirectRescheduleFlow) {
      setError('');
    }
    try {
      const payload = await fetchBookingActionContext(token, initialAction);
      setContext(payload);
      if (isDirectRescheduleFlow) {
        setError('');
      }
    } catch (err) {
      if (err?.payload) {
        setContext(err.payload);
      }
      setError(err?.message || 'Unable to load this interview link.');
    } finally {
      setContextLoading(false);
    }
  }, [initialAction, isDirectRescheduleFlow, token]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (!initialAction || contextLoading || completed || !context) return;

    if (context.action_already_used) {
      showAlreadyUsedCompletedState({
        context,
        requestedAction: initialAction,
        setShowRescheduleForm,
        setResult,
        setCompletedAction,
        setCompleted,
        setError,
      });
      return;
    }

    if (initialAction === 'accept') {
      return;
    }
    if (initialAction === 'reschedule') {
      if (!context.allowed_actions?.includes('reschedule')) {
        setShowRescheduleForm(false);
        setError('Reschedule is no longer available from this link.');
        return;
      }
      setError('');
      setShowRescheduleForm(true);
      return;
    }
    if (initialAction === 'decline') {
      if (!context.allowed_actions?.includes('decline')) return;
      setPendingAction('decline');
      setDeclineReason('');
      setDeclineReasonError('');
      setConfirmOpen(true);
      return;
    }
    if (!context.allowed_actions?.includes(initialAction)) return;
    setPendingAction(initialAction);
    setConfirmOpen(true);
  }, [initialAction, contextLoading, completed, context]);

  const showFullPageLoading =
    contextLoading && !isDirectRescheduleFlow && !showRescheduleForm;

  const actor = context?.actor || 'candidate';
  const participantLabel = actor === 'candidate' ? 'Company' : 'Interviewer';
  const participantValue =
    actor === 'candidate' ? context?.company_label : context?.interviewer_name;
  const timezoneName = context?.timezone_name || context?.availability?.timezone || 'Asia/Kolkata';
  const hostSlotDurationMinutes = useMemo(
    () => resolveHostSlotDurationMinutes(context?.availability),
    [context?.availability]
  );
  const rescheduleCanSubmit =
    Boolean(selectedSlotValue) ||
    customSuggestedSlots.length > 0 ||
    String(availabilityNotes || '').trim().length >= 8;
  const shouldAutoProcessAccept =
    isDirectAcceptFlow &&
    Boolean(context) &&
    !completed &&
    !context?.token_expired &&
    !context?.action_already_used &&
    context?.allowed_actions?.includes('accept');
  const showAutoProcessAcceptLoading =
    shouldAutoProcessAccept && (!autoAcceptStartedRef.current || submitting);
  const rescheduleBlockedByContext =
    Boolean(context) &&
    !contextLoading &&
    (Boolean(context.action_already_used) ||
      !context.allowed_actions?.includes('reschedule'));
  const activeRescheduleForm = showRescheduleForm && !rescheduleBlockedByContext;

  const openConfirm = (action) => {
    if (context?.action_already_used) {
      showAlreadyUsedCompletedState({
        context,
        requestedAction: action,
        setShowRescheduleForm,
        setResult,
        setCompletedAction,
        setCompleted,
        setError,
      });
      return;
    }
    if (action === 'reschedule') {
      setError('');
      if (token) {
        const nextParams = new URLSearchParams({ token });
        navigate(`/interview-booking/reschedule?${nextParams.toString()}`);
        return;
      }
      setShowRescheduleForm(true);
      return;
    }
    if (action === 'decline') {
      setDeclineReason('');
      setDeclineReasonError('');
    }
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const closeRescheduleForm = () => {
    setShowRescheduleForm(false);
    setSelectedSlotValue('');
    setAvailabilityNotes('');
    setCustomSuggestedSlots([]);
    setCustomSlotsResetKey((current) => current + 1);
    setError('');
  };

  const handleClosePage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.open('', '_self');
    window.close();
  }, []);

  const handleActionSubmit = useCallback(async (action, options = {}) => {
    if (!action || !token) return;
    if (context?.action_already_used) {
      showAlreadyUsedCompletedState({
        context,
        requestedAction: action,
        setShowRescheduleForm,
        setResult,
        setCompletedAction,
        setCompleted,
        setError,
      });
      return;
    }
    const cleanDeclineReason = String(declineReason || '').trim();
    if (action === 'decline' && !cleanDeclineReason) {
      setDeclineReasonError('Please enter a reason for declining this interview.');
      return;
    }
    const renderCompletedState = Boolean(options?.renderCompletedState);
    setSubmitting(true);
    setError('');
    try {
      const payload = await submitBookingActionToken({
        token,
        action,
        declineReason: action === 'decline' ? cleanDeclineReason : '',
      });
      const successMessage = resolveBookingActionMessage(payload, actor);
      if (action === 'accept') {
        setContext((current) => {
          if (!current) return current;
          const nextStatusLabel =
            String(payload?.actor_status_label || payload?.status_label || '').trim() ||
            'Confirmed';
          return {
            ...current,
            action_already_used: true,
            status_label: nextStatusLabel,
          };
        });
        setConfirmOpen(false);
        setPendingAction('');
        if (renderCompletedState) {
          setResult(payload);
          setCompletedAction(action);
          setCompleted(true);
        }
        if (!renderCompletedState) {
          toast.success(successMessage);
        }
        return;
      }
      setResult(payload);
      setCompletedAction(action);
      setCompleted(true);
      setConfirmOpen(false);
      setPendingAction('');
      setDeclineReason('');
      setDeclineReasonError('');
    } catch (err) {
      if (err?.code === 'finalized_by_team') {
        if (err?.payload) {
          setContext(err.payload);
        }
        setConfirmOpen(false);
        setPendingAction('');
        setError('');
        return;
      }
      if (
        err?.payload?.action_already_used ||
        err?.code === 'already_used' ||
        err?.code === 'already_confirmed' ||
        err?.code === 'already_used_cross_action'
      ) {
        if (err?.payload) {
          setContext(err.payload);
        }
        showAlreadyUsedCompletedState({
          context: err?.payload || context,
          requestedAction: action,
          setShowRescheduleForm,
          setResult,
          setCompletedAction,
          setCompleted,
          setError,
        });
        setConfirmOpen(false);
        setPendingAction('');
        return;
      }
      setError(err?.message || 'Unable to complete this action.');
      setConfirmOpen(false);
      setPendingAction('');
    } finally {
      setSubmitting(false);
    }
  }, [actor, context, declineReason, token]);

  useEffect(() => {
    if (!shouldAutoProcessAccept || submitting || autoAcceptStartedRef.current) return;
    autoAcceptStartedRef.current = true;
    handleActionSubmit('accept', { renderCompletedState: true });
  }, [handleActionSubmit, shouldAutoProcessAccept, submitting]);

  const handleRescheduleSubmit = useCallback(async () => {
    if (!rescheduleCanSubmit) {
      setError('Choose an available slot or add at least one custom time option.');
      return;
    }
    if (!token) return;
    if (context?.action_already_used) {
      showAlreadyUsedCompletedState({
        context,
        requestedAction: 'reschedule',
        setShowRescheduleForm,
        setResult,
        setCompletedAction,
        setCompleted,
        setError,
      });
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const suggestedSlots = buildSuggestedSlotPayloadsFromSlots(
        customSuggestedSlots,
        timezoneName,
        hostSlotDurationMinutes
      );
      const payload = await submitBookingActionToken({
        token,
        action: 'reschedule',
        slotValue: selectedSlotValue,
        availabilityNotes: selectedSlotValue ? '' : availabilityNotes,
        suggestedSlots,
      });
      const successMessage = resolveBookingActionMessage(payload, actor);
      setContext((current) => {
        if (!current) return current;
        const nextStatusLabel =
          String(payload?.actor_status_label || payload?.status_label || '').trim() ||
          'Reschedule requested';
        return {
          ...current,
          action_already_used: true,
          status_label: nextStatusLabel,
        };
      });
      setShowRescheduleForm(false);
      setSelectedSlotValue('');
      setAvailabilityNotes('');
      setCustomSuggestedSlots([]);
      setCustomSlotsResetKey((current) => current + 1);
      setResult(payload);
      setCompletedAction('reschedule');
      setCompleted(true);
      toast.success(successMessage);
    } catch (err) {
      if (err?.code === 'finalized_by_team') {
        if (err?.payload) {
          setContext(err.payload);
        }
        setShowRescheduleForm(false);
        setError('');
        return;
      }
      if (
        err?.payload?.action_already_used ||
        err?.code === 'already_used' ||
        err?.code === 'already_confirmed' ||
        err?.code === 'already_used_cross_action'
      ) {
        if (err?.payload) {
          setContext(err.payload);
        }
        showAlreadyUsedCompletedState({
          context: err?.payload || context,
          requestedAction: 'reschedule',
          setShowRescheduleForm,
          setResult,
          setCompletedAction,
          setCompleted,
          setError,
        });
        return;
      }
      setError(err?.message || 'Unable to submit your reschedule request.');
    } finally {
      setSubmitting(false);
    }
  }, [
    actor,
    availabilityNotes,
    context,
    customSuggestedSlots,
    hostSlotDurationMinutes,
    rescheduleCanSubmit,
    selectedSlotValue,
    timezoneName,
    token,
  ]);

  const confirmCopy = useMemo(() => {
    if (pendingAction === 'accept') {
      return 'Are you sure you want to confirm this interview?';
    }
    if (pendingAction === 'next_slot') {
      return 'Share the next available interview slot options with the candidate by email.';
    }
    if (pendingAction === 'decline') {
      return 'Declining will close this interview slot and notify everyone involved.';
    }
    return '';
  }, [pendingAction]);

  if (showFullPageLoading) {
    return (
      <BookingActionShell
        title={
          directActionMode === 'accept'
            ? 'Confirming interview'
            : directActionMode === 'reschedule'
              ? 'Reschedule interview'
              : 'Loading interview'
        }
        subtitle={
          directActionMode === 'accept'
            ? null
            : directActionMode === 'reschedule'
              ? 'Fetching available interview times...'
              : 'Fetching your interview details...'
        }
      >
        <div className="li-booking-loading">
          <Loader2 className="li-booking-btn-spinner" size={28} />
        </div>
      </BookingActionShell>
    );
  }

  if (showAutoProcessAcceptLoading) {
    return (
      <BookingActionShell title="Confirming interview">
        <div className="li-booking-loading">
          <Loader2 className="li-booking-btn-spinner" size={28} />
        </div>
      </BookingActionShell>
    );
  }

  if (!token || (error && !context && !showRescheduleForm)) {
    return (
      <BookingActionShell title="Link unavailable" subtitle="We could not open this interview page.">
        <div className="li-modal-error" role="alert">
          {error || 'This interview link is missing or invalid.'}
        </div>
      </BookingActionShell>
    );
  }

  const blocked =
    Boolean(context?.action_already_used) ||
    (rescheduleBlockedByContext && Boolean(context));
  const alreadyUsedFootnote = context?.action_already_used
    ? resolveAlreadyUsedActionMessage(context, directActionMode || '', actor)
    : '';
  const completionMessage =
    completed && result ? resolveBookingActionMessage(result, actor) : '';
  const showAcceptSuccessPage =
    completed && completedAction === 'accept' && Boolean(result) && !result?.alreadyUsed;
  const showDeclineSuccessPage =
    completed && completedAction === 'decline' && Boolean(result) && !result?.alreadyUsed;
  const showTeamFinalizedActionPage = Boolean(context?.actions_finalized_by_team);
  const showConsumedActionPage =
    Boolean(context?.action_already_used) && (!completed || Boolean(result?.alreadyUsed));

  if (showAcceptSuccessPage) {
    return (
      <div className="live-interview-page li-booking-action-page li-booking-confirmed-page">
        <div className="live-interview-shell li-booking-confirmed-shell">
          <div className="li-booking-confirmed-card" role="status">
            <button
              type="button"
              className="li-booking-confirmed-close"
              aria-label="Close confirmation page"
              onClick={handleClosePage}
            >
              <X size={18} />
            </button>
            <p className="li-booking-confirmed-message">
              Your interview has been confirmed successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showDeclineSuccessPage) {
    return (
      <div className="live-interview-page li-booking-action-page li-booking-confirmed-page">
        <div className="live-interview-shell li-booking-confirmed-shell">
          <p className="li-booking-inline-notice" role="status">
            <strong>Interview declined successfully.</strong>
          </p>
        </div>
      </div>
    );
  }

  if (showTeamFinalizedActionPage) {
    return (
      <BookingActionShell title="Interview confirmed">
        <p className="li-booking-inline-notice" role="status">
          <strong>
            {context?.finalized_message ||
              'This interview has already been confirmed by the interview team. No further action is required.'}
          </strong>
        </p>
      </BookingActionShell>
    );
  }

  if (showConsumedActionPage) {
    return (
      <BookingActionShell title="Action completed">
        <p className="li-booking-inline-notice" role="status">
          <strong>
            {alreadyUsedFootnote || 'This interview action has already been completed.'}
          </strong>
        </p>
      </BookingActionShell>
    );
  }

  return (
    <BookingActionShell
      title={
        directActionMode === 'accept'
          ? 'Interview confirmation'
          : directActionMode === 'reschedule'
            ? 'Reschedule interview'
            : actor === 'candidate'
              ? 'Manage your interview'
              : 'Interview actions'
      }
      subtitle={
        directActionMode === 'accept'
          ? 'We could not complete this confirmation request from the email link.'
          : directActionMode === 'reschedule'
            ? actor === 'candidate'
              ? 'Choose a new time that works for you, or share when you are available.'
              : 'Pick a new interview time or send custom options to the candidate.'
            : actor === 'candidate'
              ? 'Confirm your attendance or request a different interview time.'
              : 'Confirm the scheduled time, request a reschedule, or share updated slot options.'
      }
    >
      {error ? (
        <div className="li-booking-inline-error" role="alert">
          {error}
        </div>
      ) : null}

      {completed && result ? (
        <div className="li-booking-inline-notice" role="status">
          <CheckCircle2 size={16} />
          <span>{completionMessage}</span>
        </div>
      ) : null}

      {contextLoading && activeRescheduleForm ? (
        <div className="li-booking-inline-notice" aria-live="polite">
          <Loader2 className="li-booking-btn-spinner" size={16} />
          Loading interview details and available times...
        </div>
      ) : null}

      {(context || activeRescheduleForm) ? (
        <BookingSummaryCard
          candidateName={context?.candidate_name}
          participantLabel={participantLabel}
          participantValue={participantValue}
          slotDisplay={context?.slot_display}
          timezoneName={context?.timezone_name || timezoneName}
          statusLabel={context?.status_label}
          jobTitle={context?.job_title}
        />
      ) : null}

      {completed ? null : activeRescheduleForm ? (
        <div className="li-booking-availability">
          <div className="li-booking-section-head">
            <h2 className="li-booking-section-title">Request a different time</h2>
            <p className="li-booking-section-copy">
              {actor === 'candidate'
                ? 'Choose a new time that works for you, or share when you are available.'
                : 'Pick a new interview time or send custom options to the candidate.'}
            </p>
          </div>

          <BookingSlotOptionList
            slots={context?.available_slots || []}
            selectedValue={selectedSlotValue}
            disabled={blocked || submitting}
            onSelect={(slot) => {
              if (!slot) {
                setSelectedSlotValue('');
                return;
              }
              setSelectedSlotValue(String(slot?.value || '').trim());
              setCustomSuggestedSlots([]);
              setCustomSlotsResetKey((current) => current + 1);
              setAvailabilityNotes('');
            }}
          />

          {(context?.available_slots || []).length ? (
            <div className="li-booking-divider">
              <span>Or choose custom times</span>
            </div>
          ) : (
            <div className="li-booking-empty-slots">
              <h2>No open slots right now</h2>
              <p>
                Share two or more times when you are available and the scheduling team will follow
                up with a new interview plan.
              </p>
            </div>
          )}

          <BookingMultiSlotPicker
            timezone={timezoneName}
            actor={actor}
            disabled={blocked || submitting}
            slotDurationMinutes={hostSlotDurationMinutes}
            resetKey={customSlotsResetKey}
            onSlotsChange={(slots) => {
              const nextSlots = Array.isArray(slots) ? slots : [];
              setCustomSuggestedSlots(nextSlots);
              if (nextSlots.length) {
                setSelectedSlotValue('');
              }
            }}
            onNotesChange={(notes) => {
              if (notes) {
                setSelectedSlotValue('');
              }
              setAvailabilityNotes(notes);
            }}
          />

          <div className="li-booking-submit-bar">
            <button
              type="button"
              className="li-booking-back-link"
              disabled={submitting}
              onClick={closeRescheduleForm}
            >
              <ArrowLeft size={16} />
              Back to interview actions
            </button>
            <button
              type="button"
              className="li-booking-btn li-booking-btn-primary"
              disabled={blocked || submitting || !rescheduleCanSubmit}
              onClick={handleRescheduleSubmit}
            >
              {submitting ? (
                <Loader2 className="li-booking-btn-spinner" size={16} />
              ) : (
                <SendHorizontal size={16} />
              )}
              {actor === 'interviewer' ? 'Send reschedule request' : 'Send availability'}
            </button>
          </div>
        </div>
      ) : (
        <div className="li-booking-action-grid">
          {context?.allowed_actions?.includes('reschedule') && (!directActionMode || directActionMode === 'reschedule') ? (
            <button
              type="button"
              className="li-booking-action-card"
              disabled={blocked || submitting}
              onClick={() => openConfirm('reschedule')}
            >
              <CalendarClock size={20} />
              <span className="li-booking-action-card-title">{BOOKING_ACTION_LABELS.reschedule}</span>
              <span className="li-booking-action-card-copy">
                {actor === 'candidate'
                  ? 'Pick a new time or share when you are available.'
                  : 'Propose a new time or send options to the candidate.'}
              </span>
            </button>
          ) : null}

          {context?.allowed_actions?.includes('accept') && (!directActionMode || directActionMode === 'accept') ? (
            <button
              type="button"
              className="li-booking-action-card is-primary"
              disabled={blocked || submitting}
              onClick={() => handleActionSubmit('accept', { renderCompletedState: true })}
            >
              <CheckCircle2 size={20} />
              <span className="li-booking-action-card-title">{BOOKING_ACTION_LABELS.accept}</span>
              <span className="li-booking-action-card-copy">
                Confirm you will attend this interview.
              </span>
            </button>
          ) : null}

          {context?.allowed_actions?.includes('decline') && (!directActionMode || directActionMode === 'decline') ? (
            <button
              type="button"
              className="li-booking-action-card"
              disabled={blocked || submitting}
              onClick={() => openConfirm('decline')}
            >
              <X size={20} />
              <span className="li-booking-action-card-title">{BOOKING_ACTION_LABELS.decline}</span>
              <span className="li-booking-action-card-copy">
                Decline this interview and let everyone involved know why.
              </span>
            </button>
          ) : null}

          {context?.allowed_actions?.includes('next_slot') && (!directActionMode || directActionMode === 'next_slot') ? (
            <button
              type="button"
              className="li-booking-action-card"
              disabled={blocked || submitting}
              onClick={() => openConfirm('next_slot')}
            >
              <CalendarClock size={20} />
              <span className="li-booking-action-card-title">{BOOKING_ACTION_LABELS.next_slot}</span>
              <span className="li-booking-action-card-copy">
                Email the candidate the next available interview slots.
              </span>
            </button>
          ) : null}
        </div>
      )}

      {completed ? (
        <p className="li-booking-footnote">
          {result?.alreadyUsed
            ? 'This link has already been used. You can close this page now.'
            : 'This response has been recorded. You can close this page now.'}
        </p>
      ) : context?.action_already_used ? (
        <p className="li-booking-footnote">
          {alreadyUsedFootnote}
        </p>
      ) : (
        <p className="li-booking-footnote">
          {directActionMode === 'accept'
            ? 'This confirmation link can be used once. Need help? Reply to your interview email.'
            : directActionMode === 'reschedule'
              ? 'This reschedule link can be used once. Need help? Reply to your interview email.'
              : 'Each action from this link can be submitted once. Need help? Reply to your interview email.'}
        </p>
      )}

      <BookingConfirmOverlay
        open={confirmOpen}
        title={BOOKING_ACTION_LABELS[pendingAction] || 'Confirm action'}
        message={confirmCopy}
        confirmLabel={BOOKING_ACTION_LABELS[pendingAction] || 'Confirm'}
        tone={pendingAction === 'decline' ? 'danger' : 'primary'}
        loading={submitting}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingAction('');
        }}
        onConfirm={() => handleActionSubmit(pendingAction)}
      >
        {pendingAction === 'decline' ? (
          <div className="mt-4 space-y-2">
            <label htmlFor="booking-decline-reason" className="block text-sm font-medium text-slate-700">
              Reason
            </label>
            <textarea
              id="booking-decline-reason"
              value={declineReason}
              onChange={(event) => {
                setDeclineReason(event.target.value);
                if (declineReasonError) setDeclineReasonError('');
              }}
              placeholder="Why are you declining this interview?"
              rows={4}
              maxLength={500}
              disabled={submitting}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
            />
            {declineReasonError ? (
              <p className="text-xs text-rose-600">{declineReasonError}</p>
            ) : null}
          </div>
        ) : null}
      </BookingConfirmOverlay>
    </BookingActionShell>
  );
}
